
/**
 * Query Proxy
 * 
 * Used to perform all AJAX queries. Contains support for JWT Access and Refresh tokens as well as retry logic and timeout/failure 
 * handlers. Uses PubSub.js to convey events. 
 * 
 * Example:
 *       td = window.dcp.getDataClass(); 
 *       td.url( "/api/v1/plants/");                                Set URL
 *       td.method( "GET");                                         Set Method
 *       td.accessToken( G.accessKoken);                            Provide JWT Access Token 
 *       td.onData( function( data) {                               Set successful callback
 *           console.log( "In Plant Data onData function: ");
 *           console.log( data)
 *       });
 *       ret = td.gather();                                         Issue request
 * 
 * @param {AccessToken} accessToken     Token to use as the Access Token when requesting data
 * @param {RefreshToken} refreshToken   Token to use as the Refresh Token when the Access Token expires
 */

/**
 *
 *
 * @class DataClassPool
 * 
 * Class Methods:
*   constructor: Initializes the DataClassPool object with an empty array dcpool.
*   ceateDataClass: Creates a new instance of DataClass and adds it to the dcpool array, returning the newly created instance.
*   getDataClass: Retrieves an available instance of DataClass from the dcpool array. If none are available, creates a new one using createDataClass. Sets the
*       `inuse` property of the retrieved instance to "in use" and returns it.
*   releaseDataClass: Marks a DataClass instance as available by setting its _inuse property to false.
*   getLength: Returns the number of instances in the dcpool array.
*
*/
class DataClassPool {
    constructor() {
        this.dcpool = [];
    }

    createDataClass() {
        const dataClassInstance = new DataClass();
        this.dcpool.push(dataClassInstance);
        return dataClassInstance;
    }

    getDataClass() {
        let availableInstance = null;
        for (const dc of this.dcpool) {
            if (!dc._inuse) {
                availableInstance = dc;
                break;
            }
        }
        if (!availableInstance) {
            availableInstance = this.createDataClass();
        }
        availableInstance._inuse = true;
        availableInstance.reset();
        return availableInstance;
    }

    releaseDataClass(id) {
        if (this.dcpool[id]) {
            this.dcpool[id]._inuse = false;
        }
    }

    getLength() {
        return this.dcpool.length;
    }
}

class DataClass
{
    constructor( accToken=null, refToken=null) 
    {
    
        this.DEF_TIMEOUT = 20000;
        this.DEF_METHOD = "GET";
        this.DEF_DATATYPE = "json";
        this.DEF_CONTENTTYPE = "json";
        this.DEF_CACHE_TTL = 3600;

        // Note RESET method also sets default values
        this._url = "";
        this._result = false;
        this._xhr = false;

        this._timeout = this.DEF_TIMEOUT;   // Milliseconds to wait for response
        this._method = this.DEF_METHOD;
        this._dataType = this.DEF_DATATYPE;
        this._contentType = this.DEF_CONTENTTYPE;

        this._onData = false;               // Callback triggered when valid data is returned
        this._onTimeout = false;            // Callback triggered when the API request times out. Passed a standard status block.
        this._onFailure = false;            // Callback triggered after an unrecoverable Failure occurs. Passed a standard status block.
        this._onError = false;              // Callback triggered after any error occurs. Passed a standard status block.
        this._onLogout = false;             // Callback triggered when the user has been logged out by the server. Passed a standard status block.
        this._onComplete = false;           // Callback triggered at the end of EVERY request regardless of the response status (i.e. both valid data and errors). Passed a standard status block.

        this._queryData = false;            // Contains all the parameters to send with the query
        this._accessToken = accToken;
        this._savedAccesstoken = false;
        this._refreshToken = refToken;
        this._refreshingAccessToken = false;
        this._tokenRefreshed = false;
        this._autoRefresh = true;           // Whether or not to automatically refresh the Access Token

        this._action = false;
        this._actionData = false;           // Action-Data flag. If true then query parameters are sent in a 'data bag' as action:xx and data:{ abc:xxx, def:xxx}. 
                                            // If false query parameters are sent as separate elements as abc:xxx, def:xxx, xyz: xxx
        this._processData = true;

        this._cacheable = false;            // True to cache the result data as *cacheKey* in a Session Cache for use later
        this._cacheKey = false;             // The name to use to cache the results in local Session Storage
        this._cacheTTL = this.DEF_CACHE_TTL;// Cache Time To Live (TTL) in seconds.
        this._localCache = false;           // True to allow results to be returned from local Session Storage cache or False to request from the server
        this._allowStale = false;           // False to force GETs and HEADs to include a _=(timestamp) to break any cached and force fresh content

        this._inuse = true;

        this.self = this;
        //This is commented out as it causes issues with the treegrid implementation
        //self = this;

        this._retryCount = 0;
        this._limit = 2;
        this._exitNow = false;
        this.saved = {};
        this.ctypes = {                     // Content Types - the form that data is sent to the server, not received from. 
            "form"      : "application/x-www-form-urlencoded",
            "formdata"  : "multipart/form-data",
            "text"      : "text/plain",
            "json"      : "application/json; charset=utf-8",
            "blob"      : "application/x-www-form-urlencoded",
            "json-blob" : "application/json; charset=utf-8"
        }

        this._id = Math.random().toString(36).slice(2, 7).toUpperCase()

    }
    /**
     * Reset all Parameters to their default values
     */
    reset()
    {
        this._url = "";
        this._result = false;
        this._xhr = false;
    
        this._timeout = this.DEF_TIMEOUT;              
        this._method = this.DEF_METHOD;
        this._dataType = this.DEF_DATATYPE;
        this._contentType = this.DEF_CONTENTTYPE;
    
        this._onData = false;
        this._onTimeout = false;
        this._onFailure = false;
        this._onError = false;
        this._onLogout = false;
        this._onComplete = false;
    
        this._queryData = false;            
        this._savedAccesstoken = false;
        this._refreshingAccessToken = false;
        this._tokenRefreshed = false;
        this._autoRefresh = true;
        this._processData = true;

        this._cacheable = true;
        this._cacheKey = false;
        this._localCache = false;
        this._allowStale = false;
    
        // Don't clear the tokens here as they are login-specifc vs request-specific
        // this._accessToken = false;
        // this._refreshToken = false;
        return this;
    }


    /**
     * Get/Set an identifer (i.e. which action to perform on the server). Only used if the data is structured as a 'bag'.
     * 
     * @param {string} value 
     */
    action( value) 
    { 
        if ( value === undefined) 
        {
            return this._action; 
        } 
        else 
        { 
            this._action = value; 
            return this;
        }
    }
    
    /**
     * Returns the jqXHR object (AJAX Response) from the server
     */
    xhr() 
    { 
        return this._xhr; 
    }
    
    /**
     * Get/Set the URL to which to send the AJAX request
     * 
     * @param {string} value 
     */
    url( value) 
    { 
        if ( value === undefined) 
        { 
            return this._url; 
        } 
        else 
        { 
            this._url = value; 
            return this;
        }
    }
    
    /**
     * The HTTP method to use to issue the request (i.e. GET, POST, PUT, DELETE). Defaults to POST. 
     * 
     * @param {string} value 
     */
    method( value) 
    { 
        if ( value === undefined) 
        { 
            return this._method; 
        } 
        else 
        { 
            this._method = value; 
            return this;
        }
    }
    
    /**
     * Get/Set the type of response we're requesting from the server. Should only use "json" or "blob". Defaults to "json". 
     * 
     * @param {string} value 
     */
     dataType( value) 
     { 
         if ( value === undefined) 
         { 
             return this._dataType; 
         } 
         else 
         { 
             if ( ["json","blob"].indexOf( value) > -1)
             {
                 this._dataType = value; 
             }
             else
             {
                 this._dataType = this.DEF_DATATYPE;
             }
             return this;
         }
     }
     
    /**
     * Get/Set the type of data we're sending to the server. Valid types are in ctypes[]
     * 
     * @param {string} value 
     */
     contentType( value) 
     { 
         if ( value === undefined) 
         { 
             return this._contentType; 
         } 
         else 
         { 
             if ( value in this.ctypes)
             {
                this._contentType = value; 
             }
             else
             {
                this._contentType = this.DEF_CONTENTTYPE;
             }
             return this;
            }
     }
     
    /**
     * Get/Set the number of milliseconds to wait for a response from the API. Defaults to 20,000 (20 seconds).
     * 
     * @param {integer} value 
     */
    timeout( value) 
    { 
        if ( value === undefined) 
        { 
            return this._timeout; 
        } 
        else 
        { 
            this._timeout = value; 
            return this;
        }
    }

    /**
     * Get/Set a boolean flag indicating if we should automatically attempt to refresh the Access Token
     * 
     * @param {boolean} value 
     */
    autoRefresh( value) 
    { 
        if ( value === undefined) 
        { 
            return this._actionData; 
        } 
        else 
        { 
            this._autoRefresh = this._parseBool( value); 
            return this;
        }
    }

    /**
     * Get/Set a boolean flag indicating if the request data should be sent as key/value elements or in a JSON 'bag'
     * with an 'action'.
     * 
     * @param {boolean} value 
     */
    actionData( value) 
    { 
        if ( value === undefined) 
        { 
            return this._actionData; 
        } 
        else 
        { 
            this._actionData = this._parseBool( value); 
            return this;
        }
    }

  /**
     * Get/Set A Boolean value specifying whether or not data sent with the request should be transformed into a query string. Default is true
     * 
     * @param {boolean} value 
     */
    processData( value) 
    { 
        if ( value === undefined) 
        { 
            return this._processData; 
        } 
        else 
        { 
            this._processData = this._parseBool( value); 
            return this;
        }
    }
          
    /**
     * Get/Set a boolean flag that, when False, will force a _=(timesstamp) on to a GET or HEAD to break any cached content.
     * 
     * @param {boolean} value 
     */
     allowStale( value) 
     { 
         if ( value === undefined) 
         { 
             return this._allowStale; 
         } 
         else 
         { 
             this._allowStale = this._parseBool( value); 
             return this;
         }
     }


    /**
     * Get/Set a boolean flag indicating if the content can be cached locally
     * 
     * @param {boolean} value 
     */
     cacheable( value) 
     { 
         if ( value === undefined) 
         { 
             return this._cacheable; 
         } 
         else 
         { 
             this._cacheable = this._parseBool( value); 
             return this;
         }
     }


    /**
     * Get/Set a boolean flag indicating if is OK to return content from the local Session Storage cache. If it is, but no cached content is found, a remote request will be issued. 
     * 
     * @param {boolean} value 
     */
     localCache( value) 
     { 
         if ( value === undefined) 
         { 
             return this._localCache; 
         } 
         else 
         { 
             this._localCache = this._parseBool( value); 
             return this;
         }
     }


    /**
     * Get/Set the name to use to store/retrieve any locally cached Session content 
     * 
     * @param {string} value 
     */
     cacheKey(value) 
     { 
         if (value === undefined) 
         { 
             return this._cacheKey; 
         } 
         else 
         { 
             this._cacheKey = value; 
             return this;
         }
     }

    /**
     * Builds the full cache key using the configured cache prefix.
     *
     * @returns {string} The fully qualified cache key with the configured prefix used to store and retrieve cached data.
    */
    cacheKeyWithPrefix()
    { 
        const prefix = CACHE_PREFIX ?? "cached";
        return `${prefix}_${this._cacheKey}`;
    }

    /**
     * Get/Set the cache TTL (Time To Live) in seconds.
     *
     * @param {number} value Cache lifetime in seconds.
    */
    cacheTTL(value) {
        if (value === undefined) {
            return this._cacheTTL;
        }
        else {
            this._cacheTTL = value;
            return this;
        }
    }

    /**
     * Get a boolean flag indicating if the request is expected to return a Blob or not. Errors that occur while retrieving a Blob will be reported in the 
     * normal JSON fashion.
     */
    isBlob() 
    { 
        return this._dataType.toLowerCase() == "blob" || this._dataType.toLowerCase() == "json-blob";
    }

    /**
     * Get a boolean flag indicating if the request is expected to return JSON or not. Errors that occur while retrieving a JSON will be reported in the 
     * normal JSON fashion.
     */
    isJSON() 
    { 
        return this._dataType.toLowerCase() == "json";
    }


    /**
     * Get/Set the onData callback function
     * 
     * @param {function} value 
     */
    onData( value) 
    { 
        if ( value === undefined) 
        { 
            return this._onData; 
        } 
        else 
        { 
            this._onData = value; 
            return this;
        }
    }
    
    /**
     * Get/Set the onTimeout callback function
     * 
     * @param {function} value 
     */
    onTimeout( value) 
    { 
        if ( value === undefined) 
        { 
            return this._onTimeout; 
        } 
        else 
        { 
            this._onTimeout = value; 
            return this;
        }
    }
    
    /**
     * Get/Set the onError callback function 
     * 
     * @param {function} value 
     */
    onError( value) 
    { 
        if ( value === undefined) 
        { 
            return this._onError; 
        } 
        else 
        { 
            this._onError = value; 
            return this;
        }
    }
    
    /**
     * Get/Set the onFailure callback function 
     * 
     * @param {function} value 
     */
    onFailure( value) 
    { 
        if ( value === undefined) 
        { 
            return this._onFailure; 
        } 
        else 
        { 
            this._onFailure = value; 
            return this;
        }
    }
    
    /**
     * Get/Set the onLogout callback function 
     * 
     * @param {function} value 
     */
    onLogout( value) 
    { 
        if ( value === undefined) 
        { 
            return this._onLogout; 
        } 
        else 
        { 
            this._onLogout = value; 
            return this;
        }
    }
    
    /**
     * Get/Set the onComplete callback function 
     * 
     * @param {function} value 
     */
    onComplete( value) 
    { 
        if ( value === undefined) 
        { 
            return this._onComplete; 
        } 
        else 
        { 
            this._onComplete = value; 
            return this;
        }
    }
    
    /**
     * Set/Get the JWT Access Token used to request data from the INET API
     * 
     * @param {string} value 
     */
    accessToken( value) 
    { 
        if ( value === undefined) 
        { 
            return this._accessToken; 
        } 
        else 
        { 
            this._accessToken = value; 
            return this;
        }
    }
    
    /**
     * Set/Get the JWT Refresh Token used to renew Access Tokens
     * 
     * @param {string} value 
     */
    refreshToken( value) 
    { 
        if ( value === undefined) 
        { 
            return this._refreshToken; 
        } 
        else 
        { 
            this._refreshToken = value; 
            return this;
        }
    }
    
    /**
     * Get the JWT token refreshed indicator showing if a new Access Token was retrieved
     */
    tokenRefreshed() 
    { 
        return this._tokenRefreshed; 
    }
    
    /**
     * Return the result of the last AJAX query
     */
    result() 
    { 
        return this._result;
    }

    /**
     * Mark the DC as available for use
     */
    release()
    {
        this._inuse = false;
    }


    /**
     * Clear out all Parameters so no data is sent in the request
     */
    clearParameters()
    {
        this._queryData = {};
        return this;
    }

    /**
     * Parameters Getter/Setter
     * 
     * Getter: Return object containing properties representing key/value tuples that will be sent as data in the request
     * Setter: If both Key and Value are specified set the Key property on the parameters object to Value. If Key is 
     *          an object then merge the Key object with the parameters object.  
     * 
     * @param {string} key 
     * @param {string} value 
     */
    parameters( key, value)
    {
        if ( this._queryData === false) this._queryData = {};
        
        // If no key, act as a getter...
        if ( key === undefined) 
        { 
            return this._queryData; 
        } 
        
        // else act as a setter...
        if ( typeof( key) == "object")
        {
            // NOTE: Can't use $.extend in the line below as it causes an array of objects to become a numbered array. 
            // this._queryData = $.extend( this._queryData, key);
            this._queryData = key;
        }
        else if ( value === undefined)
        {
            this._queryData = key;
        }
        else
        {
            this._queryData[key] = value;
        }
        return this;
    }

    /**
     * Determine if a string, boolena, or integer value is True or False
     * 
     * @param {string|boolean|integer} b 
     */
    _parseBool( b) 
    {
        return !(/^(false|no|0|off)$/i).test(b) && !!b;
    }

    /**
     * 
     * @returns Deferred        Returns a jQuery Deferred object which can be further decorated by the caller. For example:
     *                             ... this._mydata.gather().THEN( $.proxy( this.dataLoaded, this)); 
     * 
     *  While a Promise is returned data handling is best done by defining onData and onFailure functions
     * 
     */
    gather()
    {
        // If it's OK to pull content from the local cache and the content in question exists there then handle it like we retrieved it and trigger
        // all the events and publish the data. 
        const isAuthRequest = this._url && (this._url.endsWith("/authenticate/") || this._url.endsWith("/refresh/"));

        if (!isAuthRequest && this._localCache && this._cacheKey != false && this._cacheKey != '')
        {
            const cacheKeyWithPrefix = this.cacheKeyWithPrefix();
            const cached = getSession(cacheKeyWithPrefix);
            if (cached !== null)
            {
                const cachedObject = JSON.parse(cached);
                // Use cached response when it has not expired or is configured without an expiry
                if (!cachedObject.expiresAt || Date.now() < cachedObject.expiresAt){
                    const cachedData = cachedObject.data;
                    if ( this._onData) this._onData( cachedData);
                    G.ps.publish( G_ACCESS_SUCCESS, [G_ACCESS_SUCCESS, cachedData]);
                    if ( this._onComplete) this._onComplete( cachedData);
                    return cachedData;
                }
              
                // Cache expired
                const cacheKeyWithPrefix = this.cacheKeyWithPrefix();
                clearCache(cacheKeyWithPrefix);
            }
        }

        if ( !this._url) return false;
        this._retryCount = 0;
        this._exitNow = false;
        this._refreshingAccessToken = false;
        this._tokenRefreshed = false;

        // Note this directly grabs the latest Access/Refresh Tokens from Session Storage. A cleaner alternative would be to pass them in 
        // on each call.
        // this._accessToken = parseBool( sessionStorage.getItem("accessToken")) ? sessionStorage.getItem("accessToken") : undefined;
        // this._refreshToken = parseBool( sessionStorage.getItem("refreshToken")) ? sessionStorage.getItem("refreshToken") : undefined;
        this._accessToken = getSession( "accessToken");
        this._refreshToken = getSession( "refreshToken");

        return this.gatherer.bind(this)();
    }

    /**
     * The core routine which issues the async queries. SHOULD NOT BE CALLED DIRECTLY. 
     * Propeties for the query are stored in _url, _method, _timeout, _datatype and data.
     * If _accessToken has a value then it is included in the query as an Authorization Bearer header. 
     */
    gatherer()
    {
        var data = {};

        switch( this._contentType)
        {
            case "text":
            case "blob":
                data = this._queryData;
                break;

            case "form":
            case "formdata":
                data = this._queryData;
                break;

            case "json":
            case "json-blob":

                if ( this._actionData)
                {
                    data.action = this._action;
                    data.data = JSON.stringify( this._queryData);
                }
                else
                {
                    data = this._queryData;
                    if ( this._action) data.action = this._action;
                    data = JSON.stringify( data);
                }
                break;
        }

        var dt = null;
        if ( this._dataType != "blob" && this._dataType != "json-blob") dt = this._dataType;


        var hdrs = {}
        if (( this._method.toUpperCase() == "GET") && data)
        {
            hdrs["request"] = JSON.stringify( data);
            this._processData = true;
        }

        // CAUTION: dataType should be empty/null when requesting a BLOB back. For some reason any other value will cause a Binary to Blob error. 

        // AI: Added by Claude Opus 4.6 (2026-06-24, sstein) — FormData requires contentType:false and processData:false for jQuery to set multipart boundary
        var ct = this.ctypes[ this._contentType];
        var pd = this._processData;
        if ( data instanceof FormData) { ct = false; pd = false; }

        $.ajax({
            url: this._url,
            method: this._method,
            timeout: this._timeout,
            contentType: ct,
//            dataType: this._dataType,
            dataType: dt,
            processData: pd,
            data: data,
            cache: this._allowStale,
            context: this,
            headers: hdrs,

            // To handle retrieving 'blobs' we need to adjust the responseType once we know the request is successful, otherwise leave it as text in order to 
            // receive the error message as JSON
            xhr: function() {
                var xhr = new XMLHttpRequest();
                if ( this.context.isBlob()) {
                    xhr.onreadystatechange = function() 
                    {
                        if ( xhr.readyState == 2) {
                            if ( xhr.status == 200) {
                                xhr.responseType = "blob";
                            } else {
                                xhr.responseType = "text";
                            }
                        }
                    };
                }
                return xhr;
            },   

            // Before sending off the request to the API, make sure to include any required Authorization headers
            beforeSend: function( jqXHR, settings)
            {
                if ( this._accessToken) { jqXHR.setRequestHeader('Authorization', 'Bearer ' + this._accessToken);}
            },

            // Handle all error (>=400) codes received from the API or server including all application-level errors. 
            error: this.onQueryError,
            success: this.onQuerySuccess
        });           
    }


    onQuerySuccess(data, textStatus, jqXHR) 
    {
        console.log( "In Success callback");
        this._result = data;
        this._xhr = jqXHR;

        console.log( data);

        // If we're expecting a JSON response do some processing on it, if not it's probably a blob so pass it on
        if ( this._dataType == "json" )
        {
            if ( typeof( data) != "object")
            {
                ret = {"status":false, "reason":"request_format_error", "data":jqXHR.responseText, "request_status":jqXHR.status};
                if ( this._onError) this._onError( ret);
                G.ps.publish( G_ACCESS_INVALID, [G_ACCESS_INVALID, data]);
                if ( this._onComplete) this._onComplete( ret);
                return data;
            }

            // If the response is an object but is missing status, reason, or data properties then throw a INVALID event and just return the data as is. 
            // If this event is unexpected it should get picked up and redirect a user to logout.
            if ( !data || data.status == undefined || data.reason == undefined || data.data == undefined)
            {
                var ret = {"status":false, "reason":"request_error", "data":jqXHR.responseText, "request_status":jqXHR.status};
                if ( this._onError) this._onError( ret);
                G.ps.publish( G_ACCESS_INVALID, [G_ACCESS_INVALID, data]);
                if ( this._onComplete) this._onComplete( data);
                return data;
            }

            // This gets hit if we've attempted to refresh an Access Token and may need to re-issue the original request again
            if ( this._refreshingAccessToken)
            {
                this._refreshingAccessToken = false;
                if ( data.status == false)
                {
                    // Failed refreshing the Access Token so most likely the Refresh Token has expired as well. Trigger the login process. 
                    if ( this._onLogout) this._onLogout( data);
                    G.ps.publish( G_FORCELOGOUT, [G_FORCELOGOUT, data]);
                    return data
                }
                else
                { 
                    var at = 0;
                    // Suceeded refreshing the Access Token so save the new one and re-issue the original request
                    if ( at = jqXHR.getResponseHeader("access-token"))
                    {
                        this._accessToken = at;
                        this._tokenRefreshed = true;
                        
                        // Update the Global store with the refreshed Access Token
                        // G.accessToken = this._accessToken;
                        //sessionStorage.setItem("accessToken", at)                        
                        //sessionStorage.setItem( "request", data.data.request);

                        setSession( "accessToken",  at);
                        setSession( "request",      data.data.request);

                        // Let everyone know we have a new Access Token
                        G.ps.publish( G_ACCESS_REFRESH, [G_ACCESS_REFRESH, at]);
    

                        G.request = data.data.request;
                        this.restore_query();
                        return this.gatherer.bind(this)();
                    }
                    else
                    {
                        data.request_status = jqXHR.status;
                        ret = {"status":false, "reason":"invalid_response", "data":jqXHR.responseText, "request_status":jqXHR.status};
                        if ( this._onError) this._onError( ret);
                        if ( this._onFailure) this._onFailure( data);
                        if ( this._onComplete) this._onComplete( data);
                        G.ps.publish( G_ACCESS_FAILURE, [G_ACCESS_FAILURE, data]);
                        return data
                    }
                }
            }
        }

        // This is our HAPPY PLACE. Fire any onData callback after the result has come back and publish a success.
        const isSuccessResponse = jqXHR.status >= 200 && jqXHR.status < 300;
        const isAuthRequest = this._url.endsWith("/authenticate/") || this._url.endsWith("/refresh/");

        if (this._cacheable && this._cacheKey != false && this._cacheKey != '' && isSuccessResponse && !isAuthRequest)
        {
            const cache = {
                data: this._result,
                ...(this._cacheTTL && {
                    expiresAt: Date.now() + (this._cacheTTL * 1000)
                })
            };
            const cacheKeyWithPrefix = this.cacheKeyWithPrefix();
            setSession(cacheKeyWithPrefix, JSON.stringify(cache));
        }

        // AI: Modified by Claude Sonnet 4.6 (2026-05-26, sstein) — transparently decode __columnar wire data before dispatching to blade callbacks
        if ( this._result && this._result.data !== undefined) {
            this._result.data = gwDeepFromColumnar( this._result.data);
        }

        if ( this._onData) this._onData( this._result);
        G.ps.publish( G_ACCESS_SUCCESS, [G_ACCESS_SUCCESS, this._result]);                  // changed from "data" to this._result 
        if ( this._onComplete) this._onComplete( this._result);
        return this._result
    }


    onQueryError( jqXHR, textStatus, errorThrown)
    {
        console.log( "In Error callback");

        if ( jqXHR.status > 0 && jqXHR.status < 400) 
        {
            ret = {"status":false, "reason":textStatus, "data":errorThrown, "request_status":jqXHR.status};
            if ( this._onError) this._onError( ret);
            G.ps.publish( G_ACCESS_ERROR, [G_ACCESS_ERROR, errorThrown]);
            if ( this._onComplete) this._onComplete( this._result);
            return jqXHR;

            // onQuerySuccess( jqXHR.responseText, textStatus, jqXHR);
            // This should never happen as these status values will be handled by the 'success' routine but IF any are sent here then just ignore them. 
            // return jqXHR;
        }

        // Save the raw response
        this._xhr = jqXHR;

        // Handle situations where the request takes too long to complete (per _timeout). 
        if ( this._xhr.status == 0 && this._xhr.statusText == "timeout")
        {
            // Fire onTimeout callback on any jqXHR request timeout errors
            ret = {"status":false, "reason":"timeout", "data":this._xhr.responseText, "request_status":this._xhr.status};
            if ( this._onTimeout) this._onTimeout( ret);
            if ( this._onError) this._onError( ret);
            G.ps.publish( G_ACCESS_TIMEOUT, [G_ACCESS_TIMEOUT, ret]);
            if ( this._onComplete) this._onComplete( ret);
            return ret;
        }

        if ( this._xhr.status == 500)
        {
            // Handle Internal Server error which includes SQLServer Gone Away. Retry _limit times before giving up.
            if ( this._retryCount++ < this._limit && !this._exitNow)
            {
                return this.gatherer.bind(this)();
            }
            else
            {
                console.log( jqXHR.responseText)
                var ret = {"status":false, "reason":"server_error", "data":jqXHR.responseText, "request_status":jqXHR.status};
                if ( this._onError) this._onError( ret);
                G.ps.publish( G_ACCESS_ERROR, [G_ACCESS_ERROR, ret]);
                if ( this._onComplete) this._onComplete( ret);
                return ret;
            }
        }

        // Examine responseJSON to see if we've received a JSON response. If we have use it, if we haven't examine responseText
        // to see if it contains a JSON string and if so parse it. If neither, then just use responseText as is.
        if ( typeof( this._xhr.responseJSON) == "object")
        {
            var data = this._xhr.responseJSON;
        }
        else
        {
            try
            {
                var data = JSON.parse( this._xhr.responseText);
            }
            catch( e)
            {
                var data = this._xhr.responseText;
            }
        }
        this._result = data;         

        // If the response is missing status, reason, or data properties then throw a INVALID event and just return the data as is. 
        // If this event is unexpected it should get picked up and redirect a user to logout.
        if ( data == undefined || data.status == undefined || data.reason == undefined || data.data == undefined)
        {
            ret = {"status":false, "reason":"request_error", "data":this._xhr.responseText, "request_status":this._xhr.status};
            if ( this._onError) this._onError( ret);
            G.ps.publish( G_ACCESS_INVALID, [G_ACCESS_INVALID, data]);
            if ( this._onComplete) this._onComplete( ret);
            return data;
        }

        // Handle ILLEGAL ACCESS/INVALID_TOKEN responses. If this event is unexpected it should get picked up and redirect a user to logout.
        if ( data.reason == "illegal_access" || data.reason == "invalid_token" || data.reason == "invalid_user" || data.reason == "invalid_system" || data.reason == "invalid_auth_version" || data.reason == "incomplete_authentication") 
        {
            ret = {"status":false, "reason":"access_error", "data":this._xhr.responseText, "request_status":this._xhr.status};
            if ( this._onError) this._onError( ret);
            if ( data.reason == "incomplete_authentication")
            {
                G.ps.publish( G_ACCESS_INCOMPLETE, [G_ACCESS_INCOMPLETE, data]);
            }
            else
            {
                G.ps.publish( G_ACCESS_ILLEGAL, [G_ACCESS_ILLEGAL, data]);
            }
            if ( this._onComplete) this._onComplete( data);
            return data;
        }

        // Handle Data Timeout (vs Request timeout) events where the server encountered a timeout event. 
        // If this event is unexpected it should get picked up and redirect a user to a timeout handler.
        if (( data.reason == "timeout"))
        {
            // Fire any onFailure callback 
            if ( this._onTimeout) this._onTimeout( data);
            G.ps.publish( G_ACCESS_TIMEOUT, [G_ACCESS_TIMEOUT, data]);
            if ( this._onComplete) this._onComplete( data);
            return data;
        }

        console.log( data);

        // This gets hit if we've attempted to refresh an Access Token and may need to re-issue the original request again
        if ( this._refreshingAccessToken)
        {
            this._refreshingAccessToken = false;
            if ( data.status == false)
            {
                // Failed refreshing the Access Token so most likely the Refresh Token has expired as well. Trigger the logout process. 
                if ( this._onLogout) this._onLogout( data);
                G.ps.publish( G_FORCELOGOUT, [G_FORCELOGOUT, data]);
                return data
            }
            else
            { 
                // Suceeded refreshing the Access Token so save the new one and re-issue the original request
                if ( at = this._xhr.getResponseHeader("access-token"))
                {
                    this._accessToken = at;
                    this._tokenRefreshed = true;
                    
                    // Update the global store with the refreshed Access Token 
                    // G.accessToken = this._accessToken;
                    // sessionStorage.setItem( "accessToken",  at);
                    // sessionStorage.setItem( "request",      data.data.request);
                    setSession( "accessToken",  at);
                    setSession( "request",      data.data.request);

                    // Let everyone know we have a new Access Token
                    G.ps.publish( G_ACCESS_REFRESH, [G_ACCESS_REFRESH, at]);

                    //sessionStorage.setItem( "request", data.data.request);
                    this.restore_query();
                    // return gatherer();
                    return this.gatherer.bind(this)();
                }
                else
                {
                    data.request_status = jqXHR.status;
                    ret = {"status":false, "reason":"invalid_response", "data":this._xhr.responseText, "request_status":this._xhr.status};
                    if ( this._onError) this._onError( ret);
                    if ( this._onFailure) this._onFailure( data);
                    if ( this._onComplete) this._onComplete( ret);
                    G.ps.publish( G_ACCESS_FAILURE, [G_ACCESS_FAILURE, data]);
                    return data
                }
            }
        }

        // Requests that return a status of False did not complete correctly and need to be handled...
        if ( data.status == false) 
        {
            // If the request failed because the Access Token has expired, or the backend requires a 'fresh' token...
            if ( data.reason == "expired_token" || data.reason == "fresh_token_required")
            {
                if ( this._autoRefresh)
                {
                    // Let everyone know we experienced an access expiration and then to try to refresh the token
                    G.ps.publish( G_ACCESS_EXPIRED, [G_ACCESS_EXPIRED, data]);
                    this.save_query();
                    this._savedAccesstoken = this._accessToken;

                    this._accessToken = this._refreshToken;
                    this._url = "/api/v1/refresh/";
                    this._method = "POST";
                    this._timeout = 10000;
                    this._dataType = "json";
                    this._contentType = "json";
                    this._queryData = "";
                    this._refreshingAccessToken = true;
                    return this.gatherer.bind(this)();
                }
                else
                {
                    var ret = {"status":false, "reason":data.reason, "data":this._xhr.responseText, "request_status":this._xhr.status};
                    if ( this._onError) this._onError( ret);
                    G.ps.publish( G_ACCESS_EXPIRED, [G_ACCESS_EXPIRED, data]);
                    if ( this._onComplete) this._onComplete( ret);
                    return ret;
                }
            }
            // Uncommenting the following makes this 'opinionated' where a non-Success result from the server is considered a (on)Failure. 
            // leaving it commented out will fire onData in all cases where a valid response comes from the server regardless of if it failed there or not
            //
            // else
            // {
            //     data.request_status = jqXHR.status;
            //     if ( this._onFailure) this._onFailure( data);
            //     G.ps.publish( G_ACCESS_FAILURE, [G_ACCESS_FAILURE, data]);
            //     return data
            // }
        }

        // If we've made it this far than the response is a success or an 'unopinionated failure'. In either case report the results.

        // AI: Modified by Claude Sonnet 4.6 (2026-05-26, sstein) — transparently decode __columnar wire data before dispatching to blade callbacks
        if ( this._result && this._result.data !== undefined) {
            this._result.data = gwDeepFromColumnar( this._result.data);
        }

        // Fire any onData callback after the result has come back
        if ( this._onData) this._onData( this._result);
        G.ps.publish( G_ACCESS_SUCCESS, [G_ACCESS_SUCCESS, data]);
        if ( this._onComplete) this._onComplete( this._result);
        return this._result
    }



    /**
     * Save aside the properties to recreate the current AJAX request
     */
    save_query()
    {
        this.saved = {};
        this.saved.url = this._url;
        this.saved.method = this._method;
        this.saved.timeout = this._timeout;
        this.saved.dataType = this._dataType;
        this.saved.contentType = this._contentType;
        this.saved.queryData = this._queryData;
        //this.saved.isBlob = this._isBlob;
        this.saved.cacheable = this._cacheable;
        this.saved.cacheKey = this._cacheKey;
        this.saved.cacheTTL = this._cacheTTL;
        this.saved.localCache = this._localCache;
        this.saved.allowStale = this._allowStale;
        return this.saved;
    }

    /**
     * Restore the properties necessary to issue the saved AJAX request
     */
    restore_query()
    {
        this._url = this.saved.url;
        this._method = this.saved.method;
        this._timeout = this.saved.timeout;
        this._dataType = this.saved.dataType;
        this._contentType = this.saved.contentType;
        this._queryData = this.saved.queryData;
        //this._isBlob = this.saved.isBlob;
        this._cacheable = this.saved.cacheable;
        this._cacheKey = this.saved.cacheKey;
        this._cacheTTL = this.saved.cacheTTL;
        this._localCache = this.saved.localCache;
        this._allowStale = this.saved.allowStale;
        return this.saved;
    }


    /**
     * Handle the async request responses. 
     * 
     * - Handle responses from all Async queries
     * - Issue a Access Token refresh request if the token is expired and then reissue the original request. 
     * - Repeat any requests that receive a 500 Server Error response a configurable number of times. 
     * - Issue PubSub messages for key events: Server Error, Invalid Request, Illegal Access, Query Timeout, General AJAX failure, and 
     *      on Successful Query Response. 
     * - Fire Callback routines for Timeout (onTimeout), Request Failure (onFailure), Forced Logout (onLogout), and Request Success (onData).
     * 
     * @param {jqXHR} jqXHR 
     * @param {string} textStatus 
     */
    // function gather_complete( jqXHR, textStatus)

    /**
     * Handle unspecified AJAX errors by publishing an ACCESS_FAILURE event and firing any onFailure callback. 
     * NOTE: This handles AJAX failures, NOT programatic failures. 
     * 
     * @param {jqXHR} jqXHR 
     * @param {string} textStatus 
     * @param {string} errorThrown 
     */
    // function gather_fail( jqXHR, textStatus, errorThrown ) 

    // return {
    //     self: self,
    //     parameters: parameters,
    //     gather: gather,
    //     onData: onData,
    //     onTimeout: onTimeout,
    //     onError: onError,
    //     onFailure: onFailure,
    //     onLogout: onLogout,
    //     onComplete: onComplete,
    //     timeout: timeout,
    //     action: action,
    //     url: url,
    //     xhr: xhr,
    //     method: method,
    //     dataType: dataType,
    //     isBlob: isBlob,
    //     isJSON: isJSON,
    //     result: result,
    //     clearParameters: clearParameters,
    //     autoRefresh: autoRefresh,
    //     actionData: actionData,
    //     processData: processData,
    //     _parseBool: _parseBool,
    //     accessToken: accessToken,
    //     refreshToken: refreshToken,
    //     tokenRefreshed: tokenRefreshed,
    //     save_query: save_query, 
    //     restore_query: restore_query,
    //     reset: reset,
    //     allowStale: allowStale,
    //     cacheable: cacheable,
    //     cacheKey: cacheKey,
    //     cacheTTL: cacheTTL,
    //     localCache: localCache
    // };
    
};
